import React, { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import L, { LatLngExpression, MarkerCluster } from 'leaflet';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { useSearchParams } from 'react-router-dom';
import { MAP_PROFILE_ID_PARAM } from 'routes/map/list';
import { hasCoordinates } from 'utils/hasCoordinates';

import 'leaflet/dist/leaflet.css';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.css';
import './leaflet-icon';

import { useGetProfilesQuery } from 'services/moderation/moderationApiService';

// The API has no "fetch all" mode; request a high upper bound for the map view.
const MAP_PROFILES_LIMIT = 1000;
const DEFAULT_CENTER: LatLngExpression = [20, 0];
const DEFAULT_ZOOM = 2;
const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// Pixel radius within which markers collapse into one cluster.
const CLUSTER_RADIUS = 60;

// Cluster bubbles grow with the number of profiles inside them.
const CLUSTER_SIZES = [
  { maxCount: 10, size: 34 },
  { maxCount: 100, size: 42 },
  { maxCount: Infinity, size: 52 },
];

// Leaflet renders cluster bubbles from raw HTML, so the Tailwind classes live in
// this string rather than in JSX.
const createClusterIcon = (cluster: MarkerCluster) => {
  const count = cluster.getChildCount();
  const { size } = CLUSTER_SIZES.find(({ maxCount }) => count < maxCount) ?? CLUSTER_SIZES[2];

  return L.divIcon({
    html: `<div class="flex size-full items-center justify-center rounded-full border-2 border-white bg-zinc-900/90 text-xs font-semibold text-white shadow-md">${count}</div>`,
    className: '',
    iconSize: L.point(size, size),
  });
};

const MapPage: React.FC = () => {
  const { data } = useGetProfilesQuery({ limit: MAP_PROFILES_LIMIT, offset: 0 });

  const [searchParams] = useSearchParams();
  const focusedProfileId = searchParams.get(MAP_PROFILE_ID_PARAM);

  // Both instances arrive from refs after mount, hence state rather than refs:
  // the focus effect has to re-run once Leaflet hands them over.
  const [cluster, setCluster] = useState<L.MarkerClusterGroup | null>(null);
  const [focusedMarker, setFocusedMarker] = useState<L.Marker | null>(null);

  const profiles = (data ?? []).filter(hasCoordinates);

  // Expanding the cluster chain down to the profile zooms the map onto it.
  useEffect(() => {
    if (!cluster || !focusedMarker) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    // Deferred so the cluster finishes its addLayers pass (bounds, clusters) first.
    const focus = () => {
      timeoutId = setTimeout(() =>
        cluster.zoomToShowLayer(focusedMarker, () => focusedMarker.openPopup()),
      );
    };

    // react-leaflet-cluster batches addLayer calls and chunkedLoading splits them
    // further, so the marker may not be clustered yet (no __parent). zoomToShowLayer
    // needs it, so wait for the cluster to report the marker as added.
    const isClustered = (focusedMarker as L.Marker & { __parent?: unknown }).__parent;
    const onLayerAdd = (event: L.LayerEvent) => {
      if (event.layer === focusedMarker) {
        cluster.off('layeradd', onLayerAdd);
        focus();
      }
    };

    if (isClustered) {
      focus();
    } else {
      cluster.on('layeradd', onLayerAdd);
    }

    return () => {
      cluster.off('layeradd', onLayerAdd);
      clearTimeout(timeoutId);
    };
  }, [cluster, focusedMarker]);

  return (
    <div className="h-[calc(100vh-2rem)] w-full overflow-hidden rounded-md border">
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        scrollWheelZoom
        className="h-full w-full">
        <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
        <MarkerClusterGroup
          ref={setCluster}
          chunkedLoading
          maxClusterRadius={CLUSTER_RADIUS}
          showCoverageOnHover={false}
          iconCreateFunction={createClusterIcon}>
          {profiles.map((profile) => {
            const age = dayjs().diff(dayjs(profile.birthDate), 'years');
            return (
              <Marker
                key={profile.id}
                ref={profile.id === focusedProfileId ? setFocusedMarker : undefined}
                position={[profile.latitude, profile.longitude]}>
                <Popup>
                  <div className="font-medium">
                    {profile.name}, {age}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MarkerClusterGroup>
      </MapContainer>
    </div>
  );
};

export default MapPage;
